import { useCallback, useEffect, useRef } from 'react';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/buffer';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/filter';
// eslint-disable-next-line jira/import/no-restricted-import
import type { RealtimeEvent } from '@atlassian/jira-realtime/src/common/types/events.tsx';
import { REFRESH_SOURCE_REALTIME } from '../../model/constants.tsx';
import { refreshDataRequest } from '../../state/actions/app/index.tsx';
import { dispatchRealtimeEventBatch } from '../../state/actions/realtime/dispatch/index.tsx';
import { useBacklogActionCreator, useBacklogSelector } from '../../state/index.tsx';
import { getAtlassianAccountId } from '../../state/selectors/app/index.tsx';

export const DEBOUNCE_TIME_IN_MS = 500;

// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
const isActiveTab = () => !document.hidden;

export const useBatchOnReceive = () => {
	const subjectRef = useRef(new Subject<RealtimeEvent>());
	const currentUser = useBacklogSelector(getAtlassianAccountId);
	const refreshData = useBacklogActionCreator(refreshDataRequest);
	const dispatch = useBacklogActionCreator(dispatchRealtimeEventBatch);

	const pushEvent = useCallback((event: RealtimeEvent) => {
		if (!subjectRef.current.closed) {
			subjectRef.current.next(event);
		}
	}, []);

	useEffect(() => {
		const subject = subjectRef.current;
		subject
			.buffer(subject.debounceTime(DEBOUNCE_TIME_IN_MS))
			.filter(isActiveTab)
			.subscribe((events) => {
				if (!subjectRef.current.closed) {
					dispatch(events);

					const hasEventsFromOthers =
						events.filter((s) => s.payload.atlassianId !== currentUser).length > 0;
					if (hasEventsFromOthers) {
						refreshData(REFRESH_SOURCE_REALTIME);
					}
				}
			});
		return () => {
			subject.unsubscribe();
		};
	}, [currentUser, dispatch, refreshData]);

	return pushEvent;
};
